// @ts-nocheck
import { Checkbox } from '@/components/ui/checkbox';
import { hasMeetingBooked } from '@/utils/callUtils';
import CrmSendButton from '../../CrmSendButton';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CallbackScheduleDialog } from '../CallbackScheduleDialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DISPOSITION_ICONS } from '../dispositionIcons';
import { PhoneCall, ChevronDown, ChevronRight, Copy, Check, Download } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { formatDate, formatDateTime, formatDurationClock } from '@/i18n/locale';
import { resolveOutcomeMeta, DISPOSITION_VALUES } from '@/services/dialer/callOutcomeLabels';
import { getEffectiveOutcome } from '@/utils/callUtils';
import { callFailureLabel } from '@/services/dialer/callFailureReason';
import { recordingPlaybackUrl, recordingDownloadUrl } from '@/services/dialer/recordingUrl';
import { copyText } from '@/components/shared/CopyLink';
import { toast } from 'sonner';

/**
 * L'onglet Historique de la fiche contact : une boite par appel, avec son
 * resultat modifiable, son enregistrement et sa transcription.
 */

export default function ProspectCallsTab({
  prospect,
  callHistory,
  allCallsExpanded,
  setAllCallsExpanded,
  expandedCalls,
  setExpandedCalls,
  toggleCallExpanded,
  getCallStatusStyle,
  handleDispositionUpdate,
  savingDisposition,
  idLienCopieFiche,
  setIdLienCopieFiche,
}) {
  const { t } = useTranslation(['admin', 'dialer', 'common']);
  const [callbackCallId, setCallbackCallId] = useState(null);

  return (
    <div>
        <CallbackScheduleDialog
            key={callbackCallId ?? 'closed'}
            open={!!callbackCallId}
            saving={savingDisposition}
            onCancel={() => setCallbackCallId(null)}
            onConfirm={async (followUpAt) => {
                const callId = callbackCallId;
                setCallbackCallId(null);
                await handleDispositionUpdate('callback_requested', callId, { follow_up_at: followUpAt, follow_up_completed: false });
            }}
        />
        {/* #67 -- titre + toggle global sur une meme ligne */}
        <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-foreground">
                {t('admin:components.prospectDetail.activityHistory')} ({callHistory.length})
            </h3>
            {/* Replier remet aussi les depliages individuels a zero,
                sinon "Tout replier" laisserait des entrees ouvertes. */}
            {callHistory.length > 0 && (
                <button
                    type="button"
                    onClick={() => {
                        const next = !allCallsExpanded;
                        setAllCallsExpanded(next);
                        if (!next) setExpandedCalls(new Set());
                    }}
                    className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
                >
                    {allCallsExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                    {t(allCallsExpanded ? 'dialer:prospectPanel.calls.collapseAll' : 'dialer:prospectPanel.calls.expandAll')}
                </button>
            )}
        </div>
        {callHistory.length === 0 ? (
            <div className="text-center py-12">
                <PhoneCall className="w-12 h-12 text-muted-foreground/40 mx-auto mb-3" />
                <p className="text-muted-foreground">{t('admin:components.prospectDetail.noCalls')}</p>
            </div>
        ) : (
            <div className="space-y-3">
                {callHistory.map((call) => {
                    const logStyle = getCallStatusStyle(call);
                    // #67 -- une entree est depliee si le toggle global
                    // est actif OU si elle a ete ouverte a l'unite.
                    const estDepliee = allCallsExpanded || expandedCalls.has(call.id);
                    return (
                    <div key={call.id} className={`bg-card border border-border rounded-lg p-4 ${logStyle.border}`}>
                        <div
                            className="flex items-center justify-between mb-2 cursor-pointer"
                            onClick={() => toggleCallExpanded(call.id)}
                        >
                            <div className="flex items-center gap-2">
                                {/* #67 -- chevron de depliage a l'unite */}
                                {estDepliee
                                    ? <ChevronDown className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                                    : <ChevronRight className="w-3 h-3 text-muted-foreground flex-shrink-0" />}
                                <div className={`w-6 h-6 rounded-full ${logStyle.iconBg} flex items-center justify-center`}>
                                    <PhoneCall className={`w-3 h-3 ${logStyle.iconColor}`} />
                                </div>
                                {(() => {
                                    // #82/#98 -- resolution par l'autorite. L'ancien code faisait
                                    // `outcomeInfo?.label || effectiveOutcome` : toute valeur absente
                                    // du dictionnaire local ressortait BRUTE a l'ecran.
                                    const meta = resolveOutcomeMeta(getEffectiveOutcome(call), call.call_status, t);
                                    return <Badge className={meta.tone}>{meta.label}</Badge>;
                                })()}
                                {callFailureLabel(call, t) && (
                                    <span
                                        className="text-xs text-muted-foreground"
                                        title={call.sip_response_code ? `SIP ${call.sip_response_code}` : undefined}
                                    >
                                        {callFailureLabel(call, t)}
                                    </span>
                                )}
                            </div>
                            <div className="flex items-center gap-2">
                            <span className="text-sm text-muted-foreground">
                                {formatDateTime(call.created_date)}
                                {call.duration_seconds > 0 && ` · ${Math.floor(call.duration_seconds / 60)}:${(call.duration_seconds % 60).toString().padStart(2, '0')}`}
                            </span>
                            {/* #78 -- "Copy call link" sur la fiche contact, seconde
                                clause de la doleance. MEME forme de lien que
                                l'historique et que la pop-up de #76 : `?call=<id>` sur
                                l'ecran d'historique, jamais une seconde forme.
                                stopPropagation, sinon le clic replie la box.
                                Icone `Copy` et non `Link2` : ce fichier n'importe pas
                                Link2 -- un composant ne voit que SES imports. */}
                            <button
                                type="button"
                                data-testid="copier-lien-fiche"
                                title={t(idLienCopieFiche === call.id ? 'dialer:prospectPanel.calls.linkCopied' : 'dialer:prospectPanel.calls.copyLink')}
                                aria-label={t('dialer:prospectPanel.calls.copyLink')}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    const lien = `${window.location.origin}/ParallelDialer/call-history?call=${encodeURIComponent(call.id)}`;
                                    void copyText(lien).then((ok) => {
                                        // A refused copy says so and shows the link (CEO-14).
                                        if (!ok) { toast.error(t('common:copyLink.manual'), { description: lien }); return; }
                                        setIdLienCopieFiche(call.id); toast.success(t('common:copyLink.copied'));
                                        setTimeout(() => setIdLienCopieFiche((v) => (v === call.id ? null : v)), 1800);
                                    });
                                }}
                                className="w-7 h-7 flex items-center justify-center rounded-md border border-border hover:bg-accent transition-colors flex-shrink-0"
                            >
                                {idLienCopieFiche === call.id
                                    ? <Check className="w-3.5 h-3.5 text-green-600" />
                                    : <Copy className="w-3.5 h-3.5 text-muted-foreground" />}
                            </button>
                            </div>
                        </div>
                        {/* #67 -- tout ce qui suit est le DETAIL : masque en vue reduite */}
                        {estDepliee && (<>
                        {/* #82 + #98 -- la box de CETTE activite : resultat pre-rempli
                            avec l'outcome de CET appel, modifiable a la main. */}
                        <div className="mt-2 mb-2 rounded-lg border border-border bg-muted/20 p-3 flex flex-wrap items-center gap-3">
                            <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                                {t('dialer:qualification.outcome')}
                            </span>
                            <Select
                                value={DISPOSITION_VALUES.includes(getEffectiveOutcome(call)) ? getEffectiveOutcome(call) : ''}
                                onValueChange={(v) => {
                                    // RUI-05: a callback is saved only once it has a date.
                                    if (v === 'callback_requested') setCallbackCallId(call.id);
                                    else handleDispositionUpdate(v, call.id);
                                }}
                                disabled={savingDisposition}
                            >
                                <SelectTrigger className="h-8 w-[200px] text-xs" onClick={(e) => e.stopPropagation()}>
                                    <SelectValue placeholder={t('dialer:qualification.selectOutcome')} />
                                </SelectTrigger>
                                <SelectContent>
                                    {DISPOSITION_VALUES.map((value) => {
                                        const Icon = DISPOSITION_ICONS[value];
                                        const meta = resolveOutcomeMeta(value, null, t);
                                        return (
                                            <SelectItem key={value} value={value}>
                                                <span className="flex items-center gap-2">
                                                    {Icon && <Icon className="w-4 h-4" />}
                                                    {meta.label}
                                                </span>
                                            </SelectItem>
                                        );
                                    })}
                                </SelectContent>
                            </Select>
                            <label
                                className="flex items-center gap-2 text-xs text-foreground/80 cursor-pointer"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <Checkbox
                                    checked={!!hasMeetingBooked(call)}
                                    onCheckedChange={() => handleDispositionUpdate('meeting_booked', call.id)}
                                    disabled={savingDisposition}
                                    aria-label={t('dialer:status.booked')}
                                />
                                {t('dialer:status.booked')}
                            </label>
                        </div>
                        {/* 2026-09-22 -- the caller sends THIS call to the client's CRM. */}
                        <CrmSendButton
                            callId={call.id}
                            qualified={DISPOSITION_VALUES.includes(getEffectiveOutcome(call)) || !!hasMeetingBooked(call)}
                        />
                        {call.ai_summary && (
                           <div className="bg-muted/30 border border-border rounded-lg p-3 mt-2 mb-2">
                               <div className="flex items-center gap-2 mb-1">
                                   <span className="text-xs font-semibold text-foreground">🤖 {t('dialer:prospectPanel.calls.aiSummary')}</span>
                                   {call.ai_confidence && (
                                       <span className="text-xs text-foreground/70">({call.ai_confidence}%)</span>
                                   )}
                                   {call.ai_sentiment && (
                                       <Badge className={
                                           call.ai_sentiment === 'positive' ? 'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200 border-0' :
                                           call.ai_sentiment === 'negative' ? 'bg-rose-50 text-rose-700 ring-1 ring-inset ring-rose-200 border-0' :
                                           'bg-slate-100 text-slate-600 ring-1 ring-inset ring-slate-200 border-0'
                                       }>
                                           {call.ai_sentiment === 'positive' ? '😊' : call.ai_sentiment === 'negative' ? '😐' : '😶'}
                                       </Badge>
                                   )}
                               </div>
                               <p className="text-sm text-foreground">{call.ai_summary}</p>
                           </div>
                        )}
                        {call.ai_action_items && call.ai_action_items.length > 0 && (
                           <div className="bg-muted/30 border border-border rounded-lg p-3 mt-2 mb-2">
                               <div className="text-xs font-semibold text-foreground mb-2">📋 {t('dialer:prospectPanel.calls.actions')}</div>
                               <ul className="space-y-1">
                                   {call.ai_action_items.map((item, idx) => (
                                       <li key={idx} className="text-xs text-foreground flex items-start gap-2">
                                           <span className="text-muted-foreground">•</span>
                                           <span>{item}</span>
                                       </li>
                                   ))}
                               </ul>
                           </div>
                        )}
                        {call.recording_url ? (
                           <div className="mt-3 flex items-center gap-2">
                               <audio
                                   controls
                                   src={recordingPlaybackUrl(call.recording_url)}
                                   preload="none"
                                   className="flex-1 h-8"
                               />
                               <a
                                   href={recordingDownloadUrl(call.recording_url, [prospect.prospect_name, prospect.prospect_company, prospect.prospect_title].filter(Boolean).join(' - ').replace(/[/\\?%*:|"<>]/g, '_') + '.mp3')}
                                   download={[prospect.prospect_name, prospect.prospect_company, prospect.prospect_title].filter(Boolean).join(' - ').replace(/[/\\?%*:|"<>]/g, '_') + '.mp3'}
                                   className="p-2 hover:bg-accent rounded transition-colors flex-shrink-0"
                                   title={t('common:actions.download')}
                                   onClick={(e) => e.stopPropagation()}
                               >
                                   <Download className="w-4 h-4 text-muted-foreground" />
                               </a>
                           </div>
                        ) : call.duration_seconds > 0 && (
                           <div className="mt-3 text-xs text-muted-foreground italic flex items-center gap-1">
                               <div className="w-2 h-2 bg-orange-400 rounded-full animate-pulse" />
                               {t('dialer:prospectPanel.calls.recordingProcessing')}
                           </div>
                        )}
                        {/* #67 -- la doleance demande la transcription dans
                            l'entree depliee, pas seulement dans son onglet. */}
                        {call.transcription && (
                           <details className="mt-3 rounded-lg border border-border bg-muted/30 p-3">
                               <summary className="text-xs font-semibold text-foreground cursor-pointer select-none">
                                   {t('admin:components.prospectDetail.tabs.transcription')}
                               </summary>
                               <p className="text-sm text-foreground/80 mt-2 whitespace-pre-line">{call.transcription}</p>
                           </details>
                        )}
                        {call.call_notes && (
                          <p className="text-sm text-foreground/80 mt-2">{call.call_notes}</p>
                        )}
                        </>)}
                    </div>
                    );
                })}
            </div>
        )}
    </div>
  );
}
